import { useState } from "react";
import type { AdminAreaView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { Banner, Button, Drawer, Field, Input, Select, Toggle, useToast } from "@/shared/rd";
import { useAreaGovernoratesQuery } from "../services/useAreaGovernoratesQuery";
import { useCreateAreaMutation } from "../services/useCreateAreaMutation";
import { useUpdateAreaMutation } from "../services/useUpdateAreaMutation";

// Suggestion-approval mode: create a new area pre-filled from a buyer-typed
// suggestion, governorate fixed to the suggestion's, saving calls the
// approve endpoint (not create) so the suggestion gets linked to the area.
export type ApproveSuggestion = {
  governorateId: string;
  governorateName: string;
  initialNameAr: string;
  onApprove: (nameAr: string, nameEn: string) => Promise<void>;
};

type Props = {
  onClose: () => void;
  // Present → edit mode (governorate fixed); absent → create mode.
  area?: AdminAreaView;
  approveSuggestion?: ApproveSuggestion;
};

type Draft = {
  governorateId: string;
  nameAr: string;
  nameEn: string;
  isActive: boolean;
};

function initialDraft(area?: AdminAreaView, approveSuggestion?: ApproveSuggestion): Draft {
  if (approveSuggestion) {
    return {
      governorateId: approveSuggestion.governorateId,
      nameAr: approveSuggestion.initialNameAr,
      nameEn: "",
      isActive: true,
    };
  }
  return {
    governorateId: area?.governorateId ?? "",
    nameAr: area?.nameAr ?? "",
    nameEn: area?.nameEn ?? "",
    isActive: area?.isActive ?? true,
  };
}

const FORM_ID = "area-form";

/**
 * Create / edit an area — or approve a suggestion into one — in a drawer over
 * the list. On edit the governorate is immutable (the PATCH endpoint only
 * touches name/isActive), so it renders disabled. In approve mode the area is
 * created through the approve endpoint, which takes names only, so the Active
 * switch is not offered there.
 */
export function AreaEditor({ onClose, area, approveSuggestion }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const locale = useLocale();
  const isEdit = area !== undefined;
  const isApprove = approveSuggestion !== undefined;
  const { data: governorates } = useAreaGovernoratesQuery();
  const create = useCreateAreaMutation();
  const updateArea = useUpdateAreaMutation();
  const [draft, setDraft] = useState<Draft>(() => initialDraft(area, approveSuggestion));
  /** A failed save: the server's own words ("" when it had none). */
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ governorateId?: string; nameAr?: string }>({});
  const [isApprovePending, setIsApprovePending] = useState(false);

  const isPending = create.isPending || updateArea.isPending || isApprovePending;
  const update = <K extends keyof Draft>(field: K, value: Draft[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    if (field === "nameAr" || field === "governorateId") {
      const key = field as "nameAr" | "governorateId";
      setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    }
  };

  const govLabel = (id: string): string => {
    const gov = (governorates ?? []).find((g) => g.id === id);
    if (!gov) return id;
    return (locale === "ar" ? gov.nameAr : (gov.nameEn ?? gov.nameAr)) || gov.nameAr;
  };

  /** Checked on submit: each message sits under its field and says how to fix it. */
  const validate = () => {
    const next: { governorateId?: string; nameAr?: string } = {};
    if (!isEdit && !isApprove && draft.governorateId.length === 0) next.governorateId = t("areas.governorateRequired");
    if (draft.nameAr.trim().length === 0) next.nameAr = t("areas.nameArRequired");
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const savedName = () => {
    const ar = draft.nameAr.trim();
    const en = draft.nameEn.trim();
    return locale === "ar" ? ar : en || ar;
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (isPending || !validate()) return;
    setError(null);
    try {
      if (isApprove) {
        setIsApprovePending(true);
        await approveSuggestion.onApprove(draft.nameAr.trim(), draft.nameEn.trim());
      } else if (isEdit) {
        await updateArea.mutateAsync({
          id: area.id,
          nameAr: draft.nameAr.trim(),
          nameEn: draft.nameEn.trim(),
          isActive: draft.isActive,
        });
        toast.show({ message: t("areas.toastSavedNamed", { name: savedName() }) });
      } else {
        await create.mutateAsync({
          governorateId: draft.governorateId,
          nameAr: draft.nameAr.trim(),
          nameEn: draft.nameEn.trim() || undefined,
          isActive: draft.isActive,
        });
        toast.show({ message: t("areas.toastCreatedNamed", { name: savedName() }) });
      }
      onClose();
    } catch (err) {
      const detail = err instanceof ApiJsonClientError ? err.message || "" : "";
      // A name that already exists is the field's problem, fixed in the field.
      if (err instanceof ApiJsonClientError && err.status === 409) {
        setFieldErrors({ nameAr: detail || t(keys.areaSuggestions.duplicateNameError) });
      } else {
        setError(detail);
      }
    } finally {
      if (isApprove) setIsApprovePending(false);
    }
  };

  const title = isApprove
    ? t(keys.areaSuggestions.approveDialogTitle)
    : isEdit
      ? t(keys.areas.editAction)
      : t(keys.areas.addAction);
  const crumbRoot = isApprove ? t(keys.areaSuggestions.title) : t(keys.areas.title);
  const crumbLeaf = isApprove
    ? t(keys.areaSuggestions.approveAction)
    : isEdit
      ? (locale === "ar" ? area.nameAr : (area.nameEn ?? area.nameAr))
      : t("areas.newCrumb");

  const govOptions = isEdit || isApprove
    ? [
        {
          value: draft.governorateId,
          label: isApprove ? approveSuggestion.governorateName : govLabel(draft.governorateId),
        },
      ]
    : (governorates ?? []).map((gov) => ({ value: gov.id, label: govLabel(gov.id) }));

  return (
    <Drawer
      open
      onClose={onClose}
      closeLabel={t(keys.common.close)}
      crumb={`${crumbRoot} / ${crumbLeaf}`}
      badge="map"
      title={title}
      sub={isApprove ? t("areaSuggestions.approveDrawerSub") : t("areas.drawerSub")}
      width={480}
      actions={
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          size="sm"
          loading={isPending}
          loadingLabel={isApprove ? t("areaSuggestions.approving") : isEdit ? t(keys.areas.saving) : t("areas.creating")}
        >
          {isApprove ? t("areaSuggestions.approveAndCreate") : t(keys.common.save)}
        </Button>
      }
    >
      <form
        id={FORM_ID}
        noValidate
        onSubmit={(event) => void handleSubmit(event)}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        {error !== null ? (
          <Banner
            tone="error"
            title={
              isApprove
                ? t("areaSuggestions.approveFailedTitle")
                : isEdit
                  ? t("areas.saveFailedTitle")
                  : t("areas.createFailedTitle")
            }
            actions={
              <Button size="sm" type="submit" loading={isPending} loadingLabel={t(keys.shell.retrying)}>
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {[error, t("areas.nothingChanged")].filter(Boolean).join(" ")}
          </Banner>
        ) : null}

        <Field
          label={t(keys.areas.governorateLabel)}
          htmlFor="area-governorate"
          help={
            isEdit
              ? t("areas.governorateLockedHelp")
              : isApprove
                ? t("areaSuggestions.governorateLockedHelp")
                : undefined
          }
          error={fieldErrors.governorateId}
        >
          <Select
            id="area-governorate"
            name="governorateId"
            value={draft.governorateId}
            disabled={isEdit || isApprove}
            required
            placeholder={isEdit || isApprove ? undefined : t("areas.governoratePlaceholder")}
            options={govOptions}
            onChange={(event) => update("governorateId", event.target.value)}
            style={{ maxWidth: 280 }}
          />
        </Field>

        <Field label={t(keys.areas.nameArLabel)} htmlFor="area-name-ar" error={fieldErrors.nameAr}>
          <Input
            id="area-name-ar"
            name="nameAr"
            dir="rtl"
            autoFocus
            required
            invalid={Boolean(fieldErrors.nameAr)}
            value={draft.nameAr}
            onChange={(event) => update("nameAr", event.target.value)}
          />
        </Field>

        <Field label={t("areas.nameEnOptionalLabel")} htmlFor="area-name-en">
          <Input
            id="area-name-en"
            name="nameEn"
            dir="ltr"
            value={draft.nameEn}
            onChange={(event) => update("nameEn", event.target.value)}
          />
        </Field>

        {isApprove ? null : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
            <Toggle
              checked={draft.isActive}
              label={t(keys.areas.activeLabel)}
              onChange={(next) => update("isActive", next)}
            />
            <span>{t(keys.areas.activeLabel)}</span>
          </div>
        )}
      </form>
    </Drawer>
  );
}
